import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, LogIn, Menu, MoonStar, UserCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthProvider';
import LanguageSwitcher from '../common/LanguageSwitcher';

const Navigation: React.FC = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const isZh = i18n.language.startsWith('zh');
  const { enabled, loading, user } = useAuth();

  const items = [
    { path: '/reading', en: 'Reading', zh: '占卜' },
    { path: '/library', en: 'Cards', zh: '牌库' },
    { path: '/journal', en: 'History', zh: '历史解读' },
    { path: '/learning', en: 'Learn', zh: '学习' },
    { path: '/pricing', en: 'Pricing', zh: '价格方案' },
  ];

  useEffect(() => setIsOpen(false), [location.pathname]);

  const active = (path: string) => location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-tarot-ink/90 backdrop-blur-xl">
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8" aria-label={isZh ? '主导航' : 'Main navigation'}>
        <Link to="/" className="group flex min-w-0 items-center gap-3" aria-label={isZh ? '塔罗奥秘首页' : 'Tarot Mystique home'}>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-tarot-gold/40 bg-tarot-gold/10 text-tarot-gold transition-colors group-hover:bg-tarot-gold/15">
            <MoonStar className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block truncate font-mystical text-lg text-tarot-starLight">{isZh ? '塔罗奥秘' : 'Tarot Mystique'}</span>
            <span className="hidden text-[10px] uppercase tracking-[0.19em] text-tarot-starLight/50 sm:block">{isZh ? '以牌为镜' : 'A practice of reflection'}</span>
          </span>
        </Link>

        <div className="hidden items-center gap-0.5 lg:flex">
          {items.map((item) => (
            <Link key={item.path} to={item.path} className={`nav-link text-sm ${active(item.path) ? 'active' : ''}`} aria-current={active(item.path) ? 'page' : undefined}>
              {isZh ? item.zh : item.en}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {enabled && !loading && (
            <Link
              to="/account"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-white/15 px-3 text-sm font-semibold text-tarot-starLight transition-colors hover:bg-white/5"
            >
              {user ? <UserCircle className="h-4 w-4" aria-hidden="true" /> : <LogIn className="h-4 w-4" aria-hidden="true" />}
              <span className="hidden sm:inline">{user ? (isZh ? '账户' : 'Account') : (isZh ? '登录' : 'Sign in')}</span>
            </Link>
          )}
          <Link to="/reading" className="hidden rounded-full bg-tarot-gold px-4 py-2 text-sm font-semibold text-tarot-ink transition-colors hover:bg-tarot-parchment sm:inline-flex">
            {isZh ? '开始占卜' : 'Begin a reading'}
          </Link>
          <button
            type="button"
            onClick={() => setIsOpen((value) => !value)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-tarot-starLight lg:hidden"
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
            aria-label={isOpen ? (isZh ? '关闭菜单' : 'Close menu') : (isZh ? '打开菜单' : 'Open menu')}
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {isOpen && (
        <div id="mobile-navigation" className="border-t border-white/10 bg-tarot-ink px-4 py-4 lg:hidden">
          <div className="mx-auto grid max-w-7xl gap-1">
            {items.map((item) => (
              <Link key={item.path} to={item.path} className={`flex items-center justify-between rounded-xl px-4 py-3 text-sm ${active(item.path) ? 'bg-tarot-gold/10 text-tarot-gold' : 'text-tarot-starLight/80 hover:bg-white/5'}`}>
                <span>{isZh ? item.zh : item.en}</span>
                <BookOpen className="h-4 w-4 opacity-40" aria-hidden="true" />
              </Link>
            ))}
            {enabled && !loading && (
              <Link to="/account" className="flex items-center justify-between rounded-xl px-4 py-3 text-sm text-tarot-starLight/80 hover:bg-white/5">
                <span>{user ? (isZh ? '账户' : 'Account') : (isZh ? '登录' : 'Sign in')}</span>
                {user ? <UserCircle className="h-4 w-4 opacity-40" aria-hidden="true" /> : <LogIn className="h-4 w-4 opacity-40" aria-hidden="true" />}
              </Link>
            )}
            <Link to="/reading" className="mystical-button mt-3 w-full">{isZh ? '开始免费占卜' : 'Start a free reading'}</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
